import { Box, IconButton, useTheme, Menu, MenuItem, Typography, Avatar, Tooltip, Divider, Button, useMediaQuery, Badge } from "@mui/material";
import { useState, useEffect, memo } from "react";
import {tokens } from "../../theme";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import LoginModal from "../../components/modals/LoginModal";
import TokenPriceBar from "../../components/display/TokenPriceBar";
import authService from "../../services/AuthService";
import splinterlandsService from "../../services/SplinterlandsService";
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import ShoppingCard from "../../components/display/ShoppingCard";
import ListingQueue from "../../components/display/ListingQueue";

const Topbar = ({ setIsSidebar, isSidebar }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Add media query for responsive design
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  
  // Auth-Status
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasKeychain, setHasKeychain] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  // Splinterlands-Daten
  const [, setPlayerData] = useState(null);
  const [balances, setBalances] = useState({
    sps: 0,
    dec: 0,
    voucher: 0,
    credits: 0
  });
  
  // UI-Status
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [refreshing, setRefreshing] = useState(false);
  
  // Mobiles Gerät Erkennung
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    setIsMobileDevice(/android|iPad|iPhone|iPod|webOS|BlackBerry|Windows Phone/i.test(userAgent));
  }, []);
  
  // Auth-Service abonnieren
  useEffect(() => {
    const unsubscribe = authService.subscribe((data) => {
      setIsLoggedIn(data.isLoggedIn);
      setCurrentUser(data.currentUser);
      setHasKeychain(data.hasKeychain);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Splinterlands-Service abonnieren
  useEffect(() => {
    const unsubscribe = splinterlandsService.subscribe((data) => {
      setPlayerData(data.playerData);
      setBalances(data.balances);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Menü öffnen/schließen
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Login-Modal öffnen
  const handleLoginClick = () => {
    setLoginModalOpen(true);
  };
  
  // Logout
  const handleLogout = () => {
    authService.logout();
    handleMenuClose();
  };
  
  // Balances aktualisieren
  const handleRefreshBalances = async () => {
    setRefreshing(true);
    try {
      await splinterlandsService.refreshBalances();
    } catch (err) {
      console.error('Error refreshing balances:', err);
    } finally {
      setRefreshing(false);
    }
  };
  
  // Helper-Funktionen zum Formatieren von Balances
  const formatBalance = (value) => {
    if (!value && value !== 0) return '0';
    return parseFloat(value).toLocaleString('en-US', { maximumFractionDigits: 3 });
  };
  
  const formatShortBalance = (value) => {
    if (!value && value !== 0) return '0';
    const num = parseFloat(value);
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toFixed(1);
  };
  
  // Avatar letter from username
  const getAvatarLetter = (username) => {
    return username ? username.charAt(0).toUpperCase() : 'U';
  };

  return (
    <Box 
      display="flex" 
      justifyContent="space-between"
      alignItems="center" 
      p={isMobile ? 0.5 : 2}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: isMobile && isSidebar ? 1050 : 1000,
        backgroundColor: colors.primary[400],
        boxShadow: 1,
        height: isMobile ? 48 : 'auto',
        overflow: 'hidden'
      }}
    >
      {/* MENU BUTTON & HOME - Left side of topbar */}
      <Box display="flex" alignItems="center" gap={1}>
        {isMobile && (
          <IconButton 
            onClick={() => setIsSidebar(!isSidebar)}
            sx={{ 
              ml: 0.5,
              color: colors.grey[100],
              padding: isVerySmallScreen ? 0.5 : 1
            }}
            size={isVerySmallScreen ? "small" : "medium"}
          >
            <MenuOutlinedIcon fontSize={isVerySmallScreen ? "small" : "medium"} />
          </IconButton>
        )}

      
      </Box>

      {/* PRICE DISPLAY - Center of topbar - Only shown on non-mobile devices */}
      {!isMobile && (
        <Box 
          display="flex" 
          alignItems="center" 
          sx={{ 
            mx: 2,
            flexGrow: 0,
            justifyContent: 'center' 
          }}
        >
          <TokenPriceBar />
        </Box>
      )}

      {/* SPACER - Only needed on mobile */}
      {isMobile && <Box flexGrow={1} />}

      {/* ACTION ICONS - Right side of topbar */}
      <Box 
        display="flex" 
        alignItems="center"
        gap={isMobile ? 0 : 1}
        sx={{ 
          mr: isMobile ? 0.5 : 0
        }}
      >
        {/* Shopping Card Icon - verwendet den CartContext */}
        <Box mr={isMobile ? 0 : 1} display="flex" alignItems="center">
          <ShoppingCard />
        </Box>

        {/* Listing Queue Icon */}
        <Box mr={isMobile ? 0 : 1} display="flex" alignItems="center">
          <ListingQueue />
        </Box>

        {/* Benutzer-Account - kompaktere Darstellung */}
        {!isLoggedIn ? (
          <>
            <Tooltip title={hasKeychain ? "Log In" : isMobileDevice ? "Hive Keychain App" : "Hive Keychain Required"}>
              <IconButton 
                onClick={handleLoginClick}
                size={isVerySmallScreen ? "small" : "medium"}
                sx={{ padding: isMobile ? 0.5 : 1 }}
              >
                <PersonOutlinedIcon fontSize={isVerySmallScreen ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
            {/* Falls keine Keychain, nur auf größeren Bildschirmen anzeigen */}
            {!hasKeychain && !isMobile && !isMediumScreen && (
              <Box ml={1} display="flex" alignItems="center">
                <a
                  href="https://hive-keychain.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Button variant="outlined" color="secondary" size="small">
                    Install Hive Keychain
                  </Button>
                </a>
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Balance Display - nur auf größeren Bildschirmen */}
            {!isMobile && isLoggedIn && (
              <Box 
                display="flex" 
                alignItems="center" 
                gap={1.5}
                sx={{
                  mr: 2,
                  padding: '6px 12px',
                  backgroundColor: colors.primary[500],
                  borderRadius: '8px',
                  border: `1px solid ${colors.primary[300]}`
                }}
              >
                {/* DEC Balance */}
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold" 
                    color={colors.greenAccent[400]}
                  >
                    {formatShortBalance(balances.dec)}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color={colors.greenAccent[400]}
                    sx={{ opacity: 0.8 }}
                  >
                    DEC
                  </Typography>
                </Box>
                
                {/* Divider */}
                <Box 
                  sx={{ 
                    width: '1px', 
                    height: '20px', 
                    backgroundColor: colors.primary[300] 
                  }} 
                />
                
                {/* Credits Balance */}
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold" 
                    color="#FFD700"
                  >
                    {formatShortBalance(balances.credits)}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="#FFD700"
                    sx={{ opacity: 0.8 }}
                  >
                    Credits
                  </Typography>
                </Box>
              </Box>
            )}
            
            <Tooltip title="Account">
              <IconButton
                onClick={handleMenuClick}
                size={isVerySmallScreen ? "small" : "medium"}
                aria-controls={menuOpen ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                sx={{ padding: isMobile ? 0.5 : 1 }}
              >
                <Avatar 
                  sx={{ 
                    width: isVerySmallScreen ? 24 : isMobile ? 28 : 32, 
                    height: isVerySmallScreen ? 24 : isMobile ? 28 : 32, 
                    fontSize: isVerySmallScreen ? '0.7rem' : isMobile ? '0.75rem' : '0.875rem',
                    fontWeight: 'bold'
                  }}
                >
                  {getAvatarLetter(currentUser?.username)}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={menuOpen}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  color: colors.grey[100],
                  mt: 1.5,
                  minWidth: 240,
                  backgroundColor: colors.primary[400],
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {currentUser?.username}
                </Typography>
              </Box>
              <Divider />
              
              {/* Splinterlands Token Balances */}
              <Divider />
              <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="bold" color={colors.grey[100]}>
                  Splinterlands Tokens
                </Typography>
                <Tooltip title="Aktualisieren">
                  <IconButton 
                    size="small" 
                    onClick={handleRefreshBalances} 
                    disabled={refreshing}
                    sx={{ color: colors.blueAccent[300] }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ px: 2, pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color={colors.redAccent[400]}>SPS:</Typography>
                  <Typography variant="body2" fontWeight="bold" color={colors.redAccent[400]}>
                    {formatBalance(balances.sps)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color={colors.greenAccent[400]}>DEC:</Typography>
                  <Typography variant="body2" fontWeight="bold" color={colors.greenAccent[400]}>
                    {formatBalance(balances.dec)}
                  </Typography>
                </Box>
                {balances.credits > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2" color="#FFD700">Credits:</Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" fontWeight="bold" color="#FFD700">
                        {formatBalance(balances.credits)}
                      </Typography>
                      <Avatar 
                        sx={{ 
                          width: 16, 
                          height: 16,
                          bgcolor: '#FFD700',
                          color: 'black',
                          fontSize: '8px',
                          fontWeight: 'bold'
                        }}
                      >$</Avatar>
                    </Box>
                  </Box>
                )}
                {balances.voucher > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color={colors.grey[300]}>Voucher:</Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" fontWeight="bold" color={colors.grey[300]}>
                        {formatBalance(balances.voucher)}
                      </Typography>
                      <Avatar 
                        sx={{ 
                          width: 16, 
                          height: 16,
                          bgcolor: colors.grey[500],
                          color: 'white',
                          fontSize: '8px',
                          fontWeight: 'bold'
                        }}
                      >V</Avatar>
                    </Box>
                  </Box>
                )}
              </Box>
              
              <Divider />
              
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 2 }} />
                Logout
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
      
      {/* Login Modal */}
      <LoginModal 
        open={loginModalOpen} 
        onClose={() => setLoginModalOpen(false)} 
      />
    </Box>
  );
};


export default memo(Topbar);
